import { useCallback } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';

import { Card } from '@/shared/components/ui/Card';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { UserCreateForm } from '@/features/users/components/UserCreateForm';
import { useUserActions, useUserDetail } from '@/features/users/hooks/useUsers';
import { toUserCreateFormState } from '@/features/users/utils/userFormMapper';
import { ErrorAlert } from '@/shared/components/alerts';

export const Route = createFileRoute('/_auth/users/$userId/edit')({
  component: EditUserRoute,
});

function EditUserRoute() {
  const { userId } = Route.useParams();
  const navigate = useNavigate();
  const numericUserId = Number(userId);
  const { data: userDetail, isLoading, error } = useUserDetail(
    Number.isSafeInteger(numericUserId) ? numericUserId : null,
  );
  const { updateUser, isUpdating } = useUserActions();

  const navigateToUsers = useCallback(() => {
    navigate({ to: '/users' });
  }, [navigate]);

  if (!Number.isSafeInteger(numericUserId)) {
    return <p className="p-6 text-destructive">Invalid user ID</p>;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <h1 className="mb-6 text-2xl font-bold">Edit User</h1>
          <Card>
            <div className="space-y-4 p-6">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-32" />
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (!userDetail) {
    return <ErrorAlert message={`Unable to load user: ${error?.message}`} />;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        <h1 className="mb-6 text-2xl font-bold">Edit User</h1>
        <Card>
          <UserCreateForm
            mode="edit"
            userId={numericUserId}
            initialValues={toUserCreateFormState(userDetail)}
            onUpdate={(user) => {
              updateUser(user, {
                onSuccess: navigateToUsers,
                onError: (updateError) => {
                  toast.error(updateError.message || 'An error occurred while updating the user');
                },
              });
            }}
            onCancel={navigateToUsers}
            isSubmitting={isUpdating}
          />
        </Card>
      </div>
    </div>
  );
}
